var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import WorkbenchLanding from './components/WorkbenchLanding';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { Spinner, SpinnerSize, MessageBar, MessageBarType, PrimaryButton } from '@fluentui/react';
import AuthService from './service/AuthService';
import DebugService from './service/DebugService';
import './components/SharedGrid.css';
initializeIcons();
export const App = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [apiToken, setApiToken] = useState(null);
    const [graphToken, setGraphToken] = useState(null);
    const [authError, setAuthError] = useState(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const TOKEN_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    const refreshTokens = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            DebugService.auth('Refreshing tokens...');
            const { apiToken: newApiToken, graphToken: newGraphToken } = yield AuthService.acquireBothTokens();
            if ((newApiToken === null || newApiToken === void 0 ? void 0 : newApiToken.accessToken) && (newGraphToken === null || newGraphToken === void 0 ? void 0 : newGraphToken.accessToken)) {
                setApiToken(newApiToken.accessToken);
                setGraphToken(newGraphToken.accessToken);
                DebugService.auth('Tokens refreshed successfully');
            }
            else {
                throw new Error('Failed to refresh tokens');
            }
        }
        catch (error) {
            DebugService.error('Token refresh failed');
            // If refresh fails, try to re-authenticate
            yield authenticate();
        }
    }), []);
    const authenticate = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsRetrying(false);
            setAuthError(null);
            setIsAuthenticated(null); // Set to loading state
            DebugService.auth('Starting authentication...');
            // Acquire both tokens in a single session to prevent multiple popups
            const { apiToken: apiTokenResult, graphToken: graphTokenResult } = yield AuthService.acquireBothTokens();
            if (!apiTokenResult || !apiTokenResult.accessToken) {
                throw new Error('Failed to acquire API token');
            }
            if (!graphTokenResult || !graphTokenResult.accessToken) {
                throw new Error('Failed to acquire Graph token');
            }
            setApiToken(apiTokenResult.accessToken);
            setGraphToken(graphTokenResult.accessToken);
            setIsAuthenticated(true);
            DebugService.auth('Authentication successful - both API and Graph tokens acquired');
        }
        catch (error) {
            DebugService.error('Authentication error');
            setAuthError(error instanceof Error ? error.message : 'Authentication failed. Please try again.');
            setIsAuthenticated(false);
        }
    });
    const handleRetry = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsRetrying(true);
        setIsAuthenticated(null);
        setApiToken(null);
        setGraphToken(null);
        setAuthError(null);
        yield authenticate();
    });
    // Set up token auto-refresh
    useEffect(() => {
        if (isAuthenticated && apiToken && graphToken) {
            const interval = setInterval(refreshTokens, TOKEN_REFRESH_INTERVAL);
            DebugService.debug('Token auto-refresh interval set up');
            return () => {
                clearInterval(interval);
                DebugService.debug('Token auto-refresh interval cleared');
            };
        }
    }, [isAuthenticated, apiToken, graphToken, refreshTokens]);
    // Initial setup - check if user is already authenticated
    useEffect(() => {
        const initializeAuth = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                setIsInitializing(true);
                // Check if user is already authenticated (has valid tokens)
                const { apiToken: cachedApiToken, graphToken: cachedGraphToken } = yield AuthService.acquireBothTokens();
                if ((cachedApiToken === null || cachedApiToken === void 0 ? void 0 : cachedApiToken.accessToken) && (cachedGraphToken === null || cachedGraphToken === void 0 ? void 0 : cachedGraphToken.accessToken)) {
                    // User already has valid tokens, no need for authentication
                    setApiToken(cachedApiToken.accessToken);
                    setGraphToken(cachedGraphToken.accessToken);
                    setIsAuthenticated(true);
                    DebugService.auth('User already authenticated with valid tokens');
                }
                else {
                    // User needs to authenticate
                    setIsAuthenticated(false);
                    DebugService.auth('User needs to authenticate');
                }
            }
            catch (error) {
                DebugService.error('Initial auth check failed');
                // If initial check fails, user needs to authenticate
                setIsAuthenticated(false);
            }
            finally {
                setIsInitializing(false);
            }
        });
        initializeAuth();
    }, []);
    // Show loading spinner while initializing
    if (isInitializing) {
        return (_jsxs("div", { className: "App", style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '16px'
            }, children: [_jsx(Spinner, { size: SpinnerSize.large, label: "Initializing..." }), _jsx("div", { style: { fontSize: '14px', color: '#666', textAlign: 'center' }, children: _jsx("div", { children: "Setting up secure connection..." }) })] }));
    }
    // Show loading spinner while authenticating
    if (isAuthenticated === null) {
        return (_jsxs("div", { className: "App", style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '16px'
            }, children: [_jsx(Spinner, { size: SpinnerSize.large, label: "Authenticating..." }), _jsxs("div", { style: { fontSize: '14px', color: '#666', textAlign: 'center' }, children: [_jsx("div", { children: "Setting up secure connection..." }), _jsx("div", { children: "Please complete the sign-in process if prompted" })] })] }));
    }
    // Show authentication prompt if user needs to authenticate
    if (isAuthenticated === false) {
        return (_jsx("div", { className: "App", style: { padding: '16px' }, children: authError ? (
            // Show error message only after user has tried to authenticate
            _jsxs(_Fragment, { children: [_jsx(MessageBar, { messageBarType: MessageBarType.error, isMultiline: true, children: authError }), _jsx("div", { style: { marginTop: '16px', textAlign: 'center' }, children: _jsx(PrimaryButton, { onClick: handleRetry, disabled: isRetrying, text: isRetrying ? "Retrying..." : "Retry Authentication" }) })] })) : (
            // Show initial authentication prompt
            _jsxs("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '24px',
                    textAlign: 'center'
                }, children: [_jsx("div", { style: { fontSize: '16px', fontWeight: 'bold', color: '#333' }, children: "Welcome to the Workbench Add-in" }), _jsx("div", { style: { fontSize: '14px', color: '#666', maxWidth: '300px' }, children: "To use this add-in, you need to sign in with your MunichRe account. This will allow you to submit placement requests and forward emails securely." }), _jsx(PrimaryButton, { onClick: authenticate, disabled: isRetrying, text: isRetrying ? "Signing In..." : "Sign In", style: { marginTop: '8px' } })] })) }));
    }
    // Show main UI only after successful authentication with both tokens
    return (_jsx("div", { className: "App", children: _jsx(WorkbenchLanding, { apiToken: apiToken, graphToken: graphToken }) }));
};
